import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import PinVerificationModal from './PinVerificationModal';
import './SendCrypto.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://globalpay-backend-v2.onrender.com/api';

export default function SendFromCardModal({ isOpen, onClose, card, onSuccess }) {
  const [formData, setFormData] = useState({
    toAddress: '',
    amount: '',
    networkFee: 0.0001
  });
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [showPinModal, setShowPinModal] = useState(false);
  const [pendingTx, setPendingTx] = useState(null);

  const availableBalance = parseFloat(card?.current_btc_balance || 0);
  const totalAmount = parseFloat(formData.amount || 0) + formData.networkFee;

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        toAddress: '',
        amount: '',
        networkFee: 0.0001
      });
      setError('');
      setIsProcessing(false);
      setShowPinModal(false);
      setPendingTx(null);
    }
  }, [isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  // Step 1: Validate and show PIN modal
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.toAddress.trim()) {
      setError('Please enter a recipient address');
      return;
    }

    const amount = parseFloat(formData.amount);
    if (isNaN(amount) || amount <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    if (totalAmount > availableBalance) {
      setError(`Insufficient balance. Need ${totalAmount.toFixed(8)} BTC, have ${availableBalance.toFixed(8)} BTC`);
      return;
    }

    // Store transaction and show PIN
    setPendingTx({
      cardId: card.id,
      toAddress: formData.toAddress.trim(),
      amount: amount
    });
    setShowPinModal(true);
  };

  // ✅ FIXED: Get token directly from localStorage
  const getValidToken = async () => {
    try {
      console.log('🔐 Getting token from localStorage...');
      
      // Get token directly from localStorage (we confirmed this works in console)
      const authKey = Object.keys(localStorage).find(key => 
        key.includes('supabase.auth.token')
      );
      
      if (!authKey) {
        console.error('❌ No auth key found in localStorage');
        throw new Error('Not logged in. Please log out and log back in.');
      }
      
      const authData = localStorage.getItem(authKey);
      
      if (!authData) {
        console.error('❌ No auth data found');
        throw new Error('No session found. Please log in again.');
      }
      
      let session;
      try {
        session = JSON.parse(authData);
      } catch (parseError) {
        console.error('❌ Failed to parse auth data:', parseError);
        throw new Error('Corrupted session. Please log out and log back in.');
      }
      
      // Check if token exists
      if (!session.access_token) {
        console.error('❌ Token is null in session');
        throw new Error('Invalid session. Please log out and log back in.');
      }
      
      // Validate JWT format (must have 3 segments: header.payload.signature)
      const token = session.access_token.trim();
      const tokenParts = token.split('.');
      
      if (tokenParts.length !== 3) {
        console.error('❌ Invalid token format. Segments:', tokenParts.length);
        throw new Error('Invalid token format. Please log out and log back in.');
      }
      
      console.log('✅ Token retrieved successfully:', {
        length: token.length,
        segments: tokenParts.length,
        firstChars: token.substring(0, 20) + '...',
        lastChars: '...' + token.substring(token.length - 20)
      });
      
      return token;
      
    } catch (error) {
      console.error('❌ Token retrieval failed:', error);
      throw error;
    }
  };

  // Step 2: After PIN verified, execute the send
  const executeSend = async () => {
    setIsProcessing(true);
    setError('');

    try {
      // ✅ Get valid token from localStorage
      const token = await getValidToken();

      console.log('📤 Sending from card:', pendingTx.cardId);
      console.log('   To:', pendingTx.toAddress);
      console.log('   Amount:', pendingTx.amount, 'BTC');

      const requestUrl = `${API_BASE_URL}/card-wallet/card-${pendingTx.cardId}/send`;
      console.log('🌐 Request URL:', requestUrl);

      const response = await fetch(requestUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          toAddress: pendingTx.toAddress,
          amount: pendingTx.amount
        })
      });

      console.log('📡 Response status:', response.status);

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          console.error('❌ 401 Unauthorized:', data);
          throw new Error('Session expired. Please log out and log back in.');
        }
        throw new Error(data.error || `Failed to send: ${response.status}`);
      }

      console.log('✅ Card send successful:', data);
      
      // Close modals and notify success
      setShowPinModal(false);
      setPendingTx(null);
      
      if (onSuccess) {
        onSuccess(data);
      }
      
      onClose();
      
    } catch (err) {
      console.error('❌ Card send failed:', err);
      
      // Better error messages
      let errorMessage = err.message || 'Failed to send transaction';
      
      if (errorMessage.includes('log out')) {
        errorMessage += '\n\nQuick fix: Clear browser cache (Ctrl+Shift+Delete) and reload.';
      }
      
      setError(errorMessage);
      setShowPinModal(false);
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePinVerified = () => {
    console.log('✅ PIN verified, executing send...');
    executeSend();
  };

  const handlePinCancel = () => {
    console.log('❌ PIN verification cancelled');
    setShowPinModal(false);
    setPendingTx(null);
    setIsProcessing(false);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content send-crypto-modal" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Send from Card ****{card?.card_number?.slice(-4)}</h2>
            <button className="close-button" onClick={onClose} disabled={isProcessing}>×</button>
          </div>

          <form onSubmit={handleSubmit} className="send-form">
            <div className="form-group">
              <label>Cryptocurrency</label>
              <select disabled className="form-control">
                <option>Bitcoin (BTC)</option>
              </select>
            </div>

            <div className="form-group">
              <label>Recipient Address *</label>
              <input
                type="text"
                name="toAddress"
                className="form-control"
                value={formData.toAddress}
                onChange={handleInputChange}
                placeholder="Enter Bitcoin address (bc1...)"
                disabled={isProcessing}
                required
              />
            </div>

            <div className="form-group">
              <label>Amount (BTC) *</label>
              <input
                type="number"
                name="amount"
                className="form-control"
                value={formData.amount}
                onChange={handleInputChange}
                placeholder="0.00000000"
                step="0.00000001"
                min="0.00000001"
                disabled={isProcessing}
                required
              />
              <small className="form-text">Available: {availableBalance.toFixed(8)} BTC</small>
            </div>

            <div className="transaction-summary">
              <div className="summary-row">
                <span>Amount</span>
                <span>{parseFloat(formData.amount || 0).toFixed(8)} BTC</span>
              </div>
              <div className="summary-row">
                <span>Network Fee (Est.)</span>
                <span>{formData.networkFee.toFixed(8)} BTC</span>
              </div>
              <div className="summary-row total">
                <span><strong>Total</strong></span>
                <span><strong>{totalAmount.toFixed(8)} BTC</strong></span>
              </div>
            </div>

            {error && (
              <div className="alert alert-error" style={{ whiteSpace: 'pre-line' }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              className="btn btn-primary btn-block"
              disabled={isProcessing || !formData.amount || !formData.toAddress}
            >
              {isProcessing ? 'Processing...' : 'Send BTC'}
            </button>
          </form>
        </div>
      </div>

      {/* PIN Verification Modal */}
      {showPinModal && (
        <PinVerificationModal
          showPinModal={showPinModal}
          setShowPinModal={setShowPinModal}
          onSuccess={handlePinVerified}
          onCancel={handlePinCancel}
          transactionType="send"
          user={null}
        />
      )}
    </>
  );
}